/**
 * useStandardUnlock — Checks if the user has met requirements to unlock standard/core mode.
 *
 * Requirements (any ONE must be met):
 * - At least 1 session exists
 * - At least 1 program exists
 * - OR a bypass entry exists in user_module_activations
 *
 * @since Progressive Interface System v2
 */

import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

const STANDARD_MODULE = "interface_standard";
const BYPASS_STEP = "bypass";

export interface StandardUnlockState {
  isUnlocked: boolean;
  hasSession: boolean;
  hasProgram: boolean;
  hasBypass: boolean;
  loading: boolean;
  bypass: () => Promise<void>;
}

export function useStandardUnlock(): StandardUnlockState {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["standard-unlock", user?.id],
    queryFn: async () => {
      if (!user) return { hasSession: false, hasProgram: false, hasBypass: false };

      const [sessions, programs, bypassCheck] = await Promise.all([
        supabase
          .from("sessions" as any)
          .select("id", { count: "exact", head: true })
          .eq("user_id", user.id),
        supabase
          .from("programs" as any)
          .select("id", { count: "exact", head: true })
          .eq("user_id", user.id),
        supabase
          .from("user_module_activations")
          .select("id", { count: "exact", head: true })
          .eq("user_id", user.id)
          .eq("module_name", STANDARD_MODULE)
          .eq("step_name", BYPASS_STEP),
      ]);

      return {
        hasSession: (sessions.count ?? 0) >= 1,
        hasProgram: (programs.count ?? 0) >= 1,
        hasBypass: (bypassCheck.count ?? 0) >= 1,
      };
    },
    enabled: !!user,
    staleTime: 2 * 60_000,
  });

  const hasSession = data?.hasSession ?? false;
  const hasProgram = data?.hasProgram ?? false;
  const hasBypass = data?.hasBypass ?? false;

  const bypass = useCallback(async () => {
    if (!user) return;
    const { error } = await supabase
      .from("user_module_activations")
      .insert({
        user_id: user.id,
        module_id: `${STANDARD_MODULE}:${BYPASS_STEP}`,
        module_name: STANDARD_MODULE,
        step_name: BYPASS_STEP,
        activation_type: "manual",
      } as any);

    if (error && error.code !== "23505") {
      if (import.meta.env.DEV) console.error("[standard-unlock] bypass error:", error);
      toast.error("Impossible de débloquer le mode Standard.");
      return;
    }

    await queryClient.invalidateQueries({ queryKey: ["standard-unlock", user.id] });
    toast.success("Mode Standard débloqué !");
  }, [user, queryClient]);

  return {
    isUnlocked: hasSession || hasProgram || hasBypass,
    hasSession,
    hasProgram,
    hasBypass,
    loading: isLoading,
    bypass,
  };
}
